import Sidebar from "@/components/Sidebar";
import { Toaster } from "sonner";
import { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { Link } from "react-router-dom";

/**
 * Round 22 addendum · mobile pass at 390px.
 * Desktop (≥ 768px): sidebar stays inline, main flex-fills.
 * Mobile (< 768px):  sidebar slides off-canvas, a black top-bar
 * with a hamburger toggles it. `overflow-x-hidden` on <main>
 * keeps the whole page inside 390px even if a specific table
 * still wants to scroll (tables get their own overflow-x-auto
 * container inside the page).
 */
export default function AppLayout({ children }) {
    const [mobileNavOpen, setMobileNavOpen] = useState(false);

    // Close the drawer on route change — otherwise tapping a link
    // navigates but the scrim stays visible over the new page.
    useEffect(() => {
        const close = () => setMobileNavOpen(false);
        window.addEventListener("popstate", close);
        return () => window.removeEventListener("popstate", close);
    }, []);

    return (
        <div className="flex min-h-screen">
            {/* Mobile top-bar — hidden on md+ */}
            <div
                data-testid="mobile-topbar"
                className="md:hidden fixed top-0 left-0 right-0 z-40 h-12 px-3 flex items-center justify-between text-white"
                style={{ background: "var(--kp-black)" }}
            >
                <button
                    data-testid="mobile-nav-toggle"
                    onClick={() => setMobileNavOpen(v => !v)}
                    aria-label={mobileNavOpen ? "Close menu" : "Open menu"}
                    className="p-2 -ml-2 text-neutral-200 hover:text-white"
                >
                    {mobileNavOpen ? <X size={20} /> : <Menu size={20} />}
                </button>
                <Link to="/" className="flex items-center gap-2">
                    <img src="/kp-logo-dark.png" alt="KP" className="h-6 w-auto" />
                    <span className="text-[10px] uppercase tracking-[0.18em] text-neutral-400 font-display">
                        MSS PNG Payroll
                    </span>
                </Link>
                <span className="w-8" aria-hidden="true" />
            </div>

            {/* Sidebar — inline on desktop, drawer on mobile */}
            <div
                data-testid="sidebar-wrapper"
                className={[
                    // desktop
                    "md:relative md:translate-x-0 md:block",
                    // mobile: fixed drawer
                    "fixed inset-y-0 left-0 z-50 transition-transform duration-200",
                    mobileNavOpen ? "translate-x-0" : "-translate-x-full",
                ].join(" ")}
            >
                <Sidebar onNavigate={() => setMobileNavOpen(false)} />
            </div>

            {/* Scrim — mobile only, when the drawer is open */}
            {mobileNavOpen && (
                <button
                    data-testid="mobile-nav-scrim"
                    onClick={() => setMobileNavOpen(false)}
                    aria-label="Close menu"
                    className="md:hidden fixed inset-0 z-40 bg-black/50"
                />
            )}

            <main
                data-testid="main-content"
                className="flex-1 min-w-0 overflow-x-hidden pt-12 md:pt-0"
            >
                {children}
            </main>
            <Toaster
                position="top-right"
                toastOptions={{
                    style: {
                        borderRadius: "3px",
                        border: "1px solid var(--kp-line)",
                        fontFamily: "Inter, sans-serif",
                    },
                }}
            />
        </div>
    );
}
